import React, { useEffect, useState } from "react";
import { ButtonComponent } from "../components/ui/buttonComponent";
import { ClipboardListIcon, FileQuestion, PenBox, Plus, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSurveyFilters } from "../hooks/fetching/getSurveyFilters";
import DropDownMenuComponent from "../components/DropDownMenu";
import { rangeSelector } from "../utils/rangeSelector";
import { Link, useNavigate } from "react-router-dom";
import { getSurveyList } from "../hooks/fetching/getSurveyList";
import { useDeleteSurvey } from "../hooks/mutations/delete-survey";

export const statusSelect = (label: string) => {
  if (label === "stopped")
    return <div className="rounded-2xl w-fit p-3 py-1.5 bg-red-100 text-red-600 font-semibold text-sm ">Stopped</div>;
  if (label === "draft")
    return (
      <div className="rounded-2xl w-fit p-3 py-1.5 bg-yellow-100 text-yellow-600 font-semibold text-sm ">Draft</div>
    );
  if (label === "") return <></>;
  return <div className="rounded-2xl w-fit p-3 py-1.5 bg-green-100 text-green-600 font-semibold text-sm ">Active</div>;
};

const SurveysPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedOrg, setSelectedOrg] = useState(0);
  const [selectedCourse, setSelectedCourse] = useState(0);
  const [orgOptions, setOrgOptions] = useState<{ option: string; label: string; courses?: string[] }[]>([
    { option: "", label: "Organizations" },
  ]);
  const [courseOptions, setCourseOptions] = useState<{ option: string; label: string }[]>([
    { option: "", label: "Courses" },
  ]);

  const { data, isFetching } = useQuery({
    queryKey: ["surveyFilters"],
    queryFn: () => getSurveyFilters(),
    retry: 1,
  });

  const {
    data: surveys,
    isFetching: isFetchingSurveys,
    refetch,
  } = useQuery({
    queryKey: ["surveyList", selectedOrg, selectedCourse],
    queryFn: () => getSurveyList(1, 10, orgOptions[selectedOrg]?.option, courseOptions[selectedCourse]?.option),
    retry: 1,
  });

  const surveyDelete = useDeleteSurvey({
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["surveyDetails"],
        exact: false,
      });
      refetch();
    },
    onFail: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    if (data && !isFetching) {
      setOrgOptions([
        { option: "", label: "Organizations", courses: [] },
        ...data?.organizations?.map((item) => ({
          option: String(item?.id),
          label: item?.name,
          courses: item?.courses ?? [],
        })),
      ]);
      setCourseOptions([
        { option: "", label: "Courses" },
        ...data?.all_courses?.map((course) => ({ option: course?.toLowerCase(), label: course?.toLowerCase() })),
      ]);
    }
  }, [data, isFetching]);

  return (
    <div className="w-full h-full flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-8 duration-400 ease-out">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-gray-900">Surveys</h1>
          <p className="text-base font-normal text-gray-500">Create and manage post-session surveys for your courses</p>
        </div>
        <ButtonComponent type="button" onClick={() => navigate("/surveys/manage")} className="flex items-center gap-1">
          <Plus className="w-5 h-5" /> Create Survey
        </ButtonComponent>
      </div>

      {data && !isFetching && (
        <div className="shadow flex flex-col gap-5 p-6 rounded-2xl bg-white">
          <h1 className="text-lg font-semibold text-gray-900">Filter by Organization & Course</h1>
          <div className="flex gap-4 items-center">
            <div className="flex flex-col gap-1">
              <p className="text-gray-500 text-sm font-medium">Organization</p>
              <DropDownMenuComponent
                options={rangeSelector(orgOptions, (i) => {
                  setSelectedOrg(i);
                })}
                selected={orgOptions[selectedOrg]?.label}
                buttonClass="py-[9px] px-[13px] rounded-xl bg-[#F8F9FA] !w-[260px]"
                menuClass="min-w-[200px] max-h-[200px] overflow-y-auto"
              />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-gray-500 text-sm font-medium">Course</p>
              <DropDownMenuComponent
                options={rangeSelector(courseOptions, (i) => setSelectedCourse(i))}
                selected={courseOptions[selectedCourse]?.label}
                buttonClass="py-[9px] px-[13px] rounded-xl bg-[#F8F9FA] !w-[260px]"
                menuClass="min-w-[200px] max-h-[200px] overflow-y-auto"
              />
            </div>
          </div>
        </div>
      )}

      {isFetchingSurveys ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="animate-spin rounded-full self-center h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : (
        <>
          {surveys?.data?.map((survey) => (
            <div className="p-6 rounded-2xl bg-white shadow flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <div className="flex gap-4 items-center">
                  <div className="w-14 h-14 rounded-full bg-xuron-orange flex justify-center items-center">
                    <ClipboardListIcon className="h-8 w-8 shrink-0 text-white" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-lg font-bold">{survey?.title}</p>
                    <p className="text-sm font-medium text-gray-500">{survey?.course_name}</p>
                    <div className="flex gap-2 items-center">
                      <p className="flex gap-2 items-center text-gray-500">
                        <FileQuestion className="text-xuron-orange h-4 w-4 shrink-0" /> {survey?.numQuestions} Questions
                        ({survey?.numMCQ} MCQ, {survey?.numOEQ} OEQ)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {statusSelect(survey?.status)}
                  <button
                    type="button"
                    onClick={() => surveyDelete.mutate({ id: survey?.id })}
                    className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center"
                  >
                    <Trash2 className="text-red-500 w-5 h-5 shrink-0" />
                  </button>
                  <Link
                    to={`/surveys/manage?id=${survey?.org_id}&courseName=${survey?.course_name}`}
                    className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center"
                  >
                    <PenBox className="text-gray-500 w-5 h-5 shrink-0" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default SurveysPage;
